import { inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';

import { Subject } from 'rxjs';
import { AudioData } from 'src/app/shared/interfaces';
import { AudioService } from 'src/app/modules/audio/services/audio/audio.service';
import { AmiraTTSService, Utterance } from '../amira-tts/amira-tts.service';
import { MAX_TTS_CHARS, replaceSpecialCharacters } from '../tts-utils';
import { TTSReadAloudService } from '../read-aloud.service';
import { VerbalService } from '../verbal.service';

/**
 * Service to interface between the read aloud service and the Amira text-to-speech service.
 */
@Injectable({ providedIn: 'root' })
export class AmiraTTSReadAloudService
  implements TTSReadAloudService, OnDestroy
{
  currentId = '';
  currIdOnStart = ''; // To track the ID of the speech that started playback
  playbackStartId = new Subject<string>();
  playbackEndId = new Subject<string>();
  isSequenceRunning = false;

  private document = inject(DOCUMENT);
  private amiraTTSService = inject(AmiraTTSService);
  private audioService = inject(AudioService);
  private verbalService = inject(VerbalService);

  constructor() {
    this.verbalService.sequenceStarted$.subscribe(id => {
      this.onSequenceOrSpeechStart(id, true);
    });

    this.verbalService.sequenceEnded$.subscribe(id => {
      this.onSequenceOrSpeechEnd(id, true);
    });

    this.verbalService.sequenceError$.subscribe(error => {
      this.onSequenceOrSpeechEnd(error.id, true);
    });
    this.verbalService.speechStarted$.subscribe(id => {
      this.onSequenceOrSpeechStart(id);
    });
    this.verbalService.speechEnded$.subscribe(id => {
      this.onSequenceOrSpeechEnd(id);
    });
    this.verbalService.speechError$.subscribe(error => {
      this.onSequenceOrSpeechEnd(error.id);
    });
  }

  /**
   * Handles the start of a sequence or speech event.
   * @param id - The identifier of the sequence or speech event, typically starting with 'read-aloud'
   * @param fromSequence - Optional boolean flag indicating if the event is triggered from a sequence. Defaults to false
   * @description Triggers playback start notification if the ID matches the expected pattern and sequence is not already running
   */
  onSequenceOrSpeechStart(id: string, fromSequence = false): void {
    if (id.startsWith('read-aloud') && !this.isSequenceRunning) {
      if (fromSequence) this.isSequenceRunning = true;
      this.currIdOnStart = this.currentId; // Store the current ID when playback starts
      this.playbackStartId.next(this.currentId);
    }
  }

  /**
   * Handles the end of a sequence or speech playback event.
   * @param id - The identifier of the completed speech or sequence, expected to start with 'read-aloud'
   * @param fromSequence - Optional flag indicating if the event was triggered from a sequence. Defaults to false
   * @remarks
   * This method will emit the current ID through playbackEndId subject and reset the current state
   * only if the ID starts with 'read-aloud' and either:
   * - The sequence is not running, or
   * - The event was triggered from a sequence (fromSequence is true)
   */
  onSequenceOrSpeechEnd(id: string, fromSequence = false): void {
    if (id.startsWith('read-aloud')) {
      // Check if the current ID matches the one that started playback before resetting the state
      if (
        (!this.isSequenceRunning || fromSequence) &&
        this.currentId &&
        this.currentId === this.currIdOnStart
      ) {
        this.postVOEnd();
      }
    }
  }

  postVOEnd(): void {
    this.playbackEndId.next(this.currentId);
    this.currentId = '';
    this.currIdOnStart = '';
    this.isSequenceRunning = false;
  }

  ngOnDestroy(): void {
    // Remove all the subscriptions to prevent memory leaks
    this.verbalService.sequenceStarted$.unsubscribe();
    this.verbalService.sequenceEnded$.unsubscribe();
    this.verbalService.sequenceError$.unsubscribe();
    this.verbalService.speechStarted$.unsubscribe();
    this.verbalService.speechEnded$.unsubscribe();
    this.verbalService.speechError$.unsubscribe();
  }

  async play(audioData: AudioData): Promise<void> {
    if (!audioData?.text) {
      console.warn('Amira TTS Service: No text provided.');
      return;
    }

    this.currentId = audioData.id;
    try {
      // Use VerbalService to process long text and get audio data/sequence
      const processedAudio = await this.verbalService.processLongText(
        audioData.text,
        audioData.text.length < MAX_TTS_CHARS ? 'read-aloud' : 'read-aloud-seq'
      );

      // Load the processed audio
      const loaded = await this.verbalService.loadBatch([processedAudio]);
      if (!loaded) {
        throw new Error(`Failed to load audio for text: ${audioData.text}`);
      }

      // check if the current ID matches the audio data ID
      // This is to ensure that we are playing the correct audio
      // and not an outdated one, especially in case of retries or delayed response from TTS service
      if (this.currentId !== audioData.id) {
        console.warn(
          'AmiraTTSReadAloudService.play: Audio ID mismatch, skipping playback.',
          {
            audioId: audioData.id,
            currentId: this.currentId,
          }
        );
        return;
      }
      // Play the audio using VerbalService
      if (this.currentId !== '') {
        await this.verbalService.play(processedAudio);
      }
    } catch (error) {
      console.error(
        '❌ [AmiraTTSReadAloudService.play] Error during playback:',
        {
          audioId: audioData.id,
          currentId: this.currentId,
          error: error instanceof Error ? error.message : error,
          stack: error instanceof Error ? error.stack : undefined,
        }
      );
      this.handleError(error as Error);
    }
  }

  handleError(error: Error): void {
    console.error(error);
    this.playbackEndId.next(this.currentId);
    this.currentId = '';
    throw error;
  }

  stop(): void {
    if (this.isSequenceRunning) {
      this.verbalService.stopCurrent();
      this.postVOEnd();
    } else if (this.audioService.isCurrentSoundActive()) {
      this.audioService.stopCurrentSound();
    }

    this.playbackEndId.next(this.currentId);
    this.currentId = '';
  }

  togglePause(): void {
    this.verbalService.togglePause();
  }

  async load(
    audioData: AudioData
  ): Promise<{ id: string; utterance: Utterance } | undefined> {
    if (!audioData?.text) {
      console.warn('No text to speak provided.');
      return;
    }

    const id = audioData.id;
    const text = replaceSpecialCharacters(audioData.text, this.document);
    const utterance = await this.amiraTTSService.getUtterance(
      text,
      false,
      'en-us',
      16000,
      true
    );

    return { id, utterance };
  }
}
